import {
  Dispatch,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { VegaChartDto } from '@asset-metrix/portal-gateway-client-js';
import {
  ErrorMessage,
  useHeightPerEntry,
  useSignal,
  VegaChart as VegaChartUi,
  VegaChartSignal,
  Widget,
  WidgetWithPaginator,
} from '@asset-metrix/portal-ui';
import { noop } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Spec, View } from 'vega';

import {
  fetchDatatableData,
  FetchDatatableDataQueryKey,
} from '../../api/portalGateway/datatableData';
import { fetchPage, PageQueryKey } from '../../api/portalGateway/page';
import { useStore } from '../../store';
import { useFilterSelections } from '../../store/hooks/useFilterSelections';
import { extractErrorMessage } from '../../utils/errorMessage';
import { prependRoleId } from '../../utils/prependRoleId';
import { PAGE_SIZE } from '../../utils/vegaChartHelpers';
import { CustomTableFilters } from '../filter/customTableFilter/CustomTableFilters';
import { PageFilters } from '../filter/PageFilters';
import { WidgetFilterContainer } from '../filter/WidgetFilterContainer';

import { ExtractedBinding } from './inputBinding/extraction';
import { VegaControlAction, VegaState } from './inputBinding/vegaControlState';
import { useFooterChartData } from './chartDataTransformHook';
import { usePageBoundaries, useWithPagination } from './paginationHooks';
import {
  BaseUseVegaChartActionProps,
  useVegaChartActions,
} from './useVegaChartActions';
import { VegaChartSignalControls } from './VegaChartSignalControls';

import styles from './vegaChart.module.scss';

export type VegaChartContentProps = {
  collapsible: boolean;
  controlDispatch: Dispatch<VegaControlAction>;
  controlState: VegaState;
  bindings: ExtractedBinding[];
  dataTestId?: string;
  enableScrollElementToTop?: boolean;
  entity: VegaChartDto;
  isFullscreen: boolean;
  labelData: object;
  pageFilterInstanceId: string;
  pageId: string;
  showPageFilter?: boolean;
  spec: Spec;
  withPagination: boolean;
  widgetInstanceId: string;
};

const getFullScreenHeight = (heightPerEntry: number, itemCount?: number) =>
  itemCount ? 100 + itemCount * heightPerEntry : undefined;

const useOnReadyControlState = (state: VegaState, viewRef: RefObject<View>) => {
  const signal = useSignal(viewRef);
  return useCallback(() => {
    for (const [signalName, signalState] of Object.entries(state)) {
      switch (signalState?.type) {
        case 'radio':
          signal(signalName, signalState.value);
          globalThis.dispatchEvent(new CustomEvent('resizevegachart'));
      }
    }
  }, [signal, state]);
};

export function VegaChartContent({
  activeCustomFilterIds,
  bindings,
  collapsible,
  controlDispatch,
  controlState,
  dataTestId,
  enableScrollElementToTop,
  entity,
  isFullscreen,
  labelData,
  pageFilterInstanceId,
  pageId,
  showPageFilter = false,
  spec,
  title,
  widgetId,
  withPagination,
  widgetInstanceId,
  ...useVegaChartActionProps
}: VegaChartContentProps & BaseUseVegaChartActionProps) {
  const { t } = useTranslation();
  const vegaViewRef = useRef<View>(null);
  const onReadyControlState = useOnReadyControlState(controlState, vegaViewRef);

  const heightPerEntry = useHeightPerEntry(spec, vegaViewRef);

  const { data: page } = useQuery({
    queryKey: prependRoleId<PageQueryKey>(['page', pageId ?? 'unknown']),
    queryFn: fetchPage,

    enabled: showPageFilter,
  });

  const {
    meta: { loaded: pageFilterLoaded },
    visualisedFilter: pageFilter,
  } = useStore((state) => {
    return state.filterData[pageFilterInstanceId];
  }) || { meta: { loaded: false }, visualisedFilter: undefined };

  const {
    meta: { loaded: tableFilterLoaded },
    visualisedFilter: tableFilter,
  } = useStore((state) => state.filterData?.[widgetInstanceId]) || {
    meta: { loaded: false },
    visualisedFilter: undefined,
  };

  const pageFilterSelections = useFilterSelections(pageFilterInstanceId);
  const tableFilterSelection = useFilterSelections(widgetInstanceId);

  const filtersAreLoaded =
    pageFilterLoaded &&
    (tableFilterLoaded || activeCustomFilterIds.length === 0);

  const isGroupDataConfigured = entity.config.vegaChartDataType === 'groupData';
  const isGroupOrFooterDataConfigured =
    isGroupDataConfigured || entity.config.vegaChartDataType === 'footerData';

  const { data, isLoading: isDataLoading } = useQuery({
    queryKey: prependRoleId<FetchDatatableDataQueryKey>([
      'datatables',
      { dataSourceType: 'datatable' },
      { pageId },
      { widgetId },
      {
        page: 0,
        pageSize: 100_000,
        pageFilter: pageFilterSelections,
        tableFilter: tableFilterSelection,
        sortItems: [],
      },
    ]),
    queryFn: fetchDatatableData,
    enabled:
      filtersAreLoaded &&
      entity.config.hasDataTable &&
      !isGroupOrFooterDataConfigured,
  });

  const { transformedData, isLoading, error } = useFooterChartData(
    widgetId,
    pageFilterInstanceId,
    widgetInstanceId,
    filtersAreLoaded &&
      entity.config.hasDataTable &&
      isGroupOrFooterDataConfigured,
    isGroupDataConfigured,
    entity.config
  );

  const rawData = useMemo(() => {
    if (entity.config.hasDataTable) {
      return isGroupOrFooterDataConfigured ? transformedData : data?.data.items;
    }

    const specRawData = spec.data?.find((d) => d.name === 'rawData');

    if (specRawData && 'values' in specRawData) {
      return specRawData.values;
    }
  }, [
    entity.config.hasDataTable,
    spec.data,
    isGroupOrFooterDataConfigured,
    transformedData,
    data?.data.items,
  ]);

  const {
    currentPage,
    onReadyCallback: onReadyPageBoundaries,
    setCurrentPage,
    itemCount,
  } = usePageBoundaries(withPagination, vegaViewRef, spec, rawData);

  useEffect(() => {
    setCurrentPage(0);
  }, [pageFilter, tableFilter]);

  useEffect(() => {
    setTimeout(() =>
      globalThis.dispatchEvent(new CustomEvent('resizevegachart'))
    );
  }, [rawData]);

  const signal = useSignal(vegaViewRef);
  const specHasPageBoundarySignal = useWithPagination(spec);

  const updatePageBoundarySignalValues = () => {
    if (isFullscreen && specHasPageBoundarySignal) {
      signal(VegaChartSignal.pageBoundaries, [0, itemCount - 1]);
    }
  };

  const Component = withPagination ? WidgetWithPaginator : Widget;

  const actions = useVegaChartActions({
    config: entity.config,
    activeCustomFilterIds,
    title,
    widgetId,
    pageFilter: pageFilterSelections,
    vegaViewRef,
    ...useVegaChartActionProps,
  });

  const columnConfigs = useMemo(
    () =>
      entity.config.customFilter.map((filter) => ({
        id: filter.id,
        customFilter: filter.filterConfig,
      })),
    [entity.config.customFilter]
  );

  if (
    (isGroupDataConfigured && !entity.config.groupedColumnForChart) ||
    error
  ) {
    const errorMessage = error
      ? extractErrorMessage(error)
      : t('app.vegaChartWidget.groupingError');

    return (
      <Widget title={title} dataTestId={dataTestId}>
        <ErrorMessage>{errorMessage}</ErrorMessage>
      </Widget>
    );
  }

  return (
    <Component
      dataTestId={dataTestId}
      title={title}
      collapsible={collapsible}
      enableScrollElementToTop={enableScrollElementToTop}
      actions={actions}
      activePage={currentPage}
      activePaginationSize={PAGE_SIZE}
      availablePaginationSizes={[PAGE_SIZE]}
      onPageChange={setCurrentPage}
      onPaginationSizeChange={noop}
      totalEntries={itemCount}
    >
      <WidgetFilterContainer>
        {showPageFilter && page != null ? (
          <PageFilters
            config={page.data.pageFilter}
            filterKey={pageFilterInstanceId}
            pageId={pageId}
            embedded={true}
          />
        ) : null}
        {activeCustomFilterIds.length > 0 && (
          <CustomTableFilters
            activeCustomFilterIds={activeCustomFilterIds}
            columnConfigs={columnConfigs}
            pageFilterInstanceId={pageFilterInstanceId}
            widgetId={widgetId}
            widgetFilterInstanceId={widgetInstanceId}
          />
        )}
      </WidgetFilterContainer>
      {bindings.length > 0 && (
        <VegaChartSignalControls
          bindings={bindings}
          controlDispatch={controlDispatch}
          controlState={controlState}
          // vega specs could potentially contain label data that is no array
          labelData={Array.isArray(labelData) ? labelData : []}
          vegaViewRef={vegaViewRef}
        />
      )}
      <VegaChartUi
        className={styles['vegaChart']}
        spec={spec}
        data={{
          labelData,
          rawData: rawData ?? [],
        }}
        onReadyCallback={(newView) => {
          onReadyPageBoundaries(newView);
          onReadyControlState();
          updatePageBoundarySignalValues();
        }}
        heightfactor={entity.config.heightfactor}
        isFullscreen={isFullscreen}
        loading={!filtersAreLoaded || isDataLoading || isLoading}
        vegaViewRef={vegaViewRef}
        minHeight={
          isFullscreen && heightPerEntry
            ? getFullScreenHeight(heightPerEntry, itemCount)
            : undefined
        }
      />
    </Component>
  );
}
